import * as zxcvbnCommonPackage from "@zxcvbn-ts/language-common";
import * as zxcvbnEnPackage from "@zxcvbn-ts/language-en";

import { colors } from "./colors";
import { PropertyStatus } from "@/types/enums";
import { FilterStatus } from "@/types/properties";

export const USER_TYPES = {
  ADMIN: "ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
  HOMEOWNER: "HOMEOWNER",
  MAINSAIL_ADMIN: "MAINSAIL_ADMIN",
} as const;

// Global (MainSail) admins have no organization of their own. They sign in
// through the MainSail admin org subdomain (e.g. mainsail.stage.projectmainsail.app),
// so their post-setup login link is built by prepending this slug to the root host.
export const MAINSAIL_ADMIN_SLUG = "mainsail";

export const STREAMLINE_ENVIRONMENTS = {
  PRODUCTION: "production",
  SANDBOX: "sandbox",
} as const;

export const DRAFT_CONVERSATION_ID = "draft";

export const USER_STATUS = {
  ACTIVE: "ACTIVE",
  UNVERIFIED: "UNVERIFIED",
  ARCHIVED: "ARCHIVED",
  ONBOARDING_CREATE_ACCOUNT: "ONBOARDING_CREATE_ACCOUNT",
  ONBOARDING_CREATE_PROPERTY: "ONBOARDING_CREATE_PROPERTY",
} as const;

export const MAX_SELECTION_LIMIT = 20;

export enum BackendPermission {
  CreatePassword = "CreatePassword",
  ResetPassword = "ResetPassword",
  AddHomeowner = "AddHomeowner",
  EditHomeowner = "EditHomeowner",
  DeactivateHomeowner = "DeactivateHomeowner",
  AddProperty = "AddProperty",
  EditProperty = "EditProperty",
  ManagePropertyEditRequests = "ManagePropertyEditRequests",
  ManagePropertyDeactivationRequests = "ManagePropertyDeactivationRequests",
  SetRevenueGoal = "SetRevenueGoal",
}

export const ACCOUNT_ONBOARDING_PATHS = {
  CREATE_YOUR_ACCOUNT: "create-your-account",
  PROPERTY_LISTING: "property-listing",
};

export const subdomainBlockedWords = [
  "www",
  "http",
  "https",
  "stage",
  "dev",
  "uat",
  "prod",
  "demo",
  "api",
  "stage-api",
  "uat-api",
  "dev-api",
  "prod-api",
  "demo-api",
  "app",
  "admin",
  "api",
  "login",
  "logout",
  "auth",
  "docs",
  "help",
  "support",
  "status",
  "cdn",
  "static",
  "assets",
  "mail",
  "smtp",
  "ftp",
  "root",
  "system",
  "null",
  "undefined",
  "test",
  "sample",
  "default",
  // Reserved for the org-less global (MainSail) admin context — must never be
  // assignable to a real organization.
  MAINSAIL_ADMIN_SLUG,
];

export const ONBOARDING_STEP_TITLES = [
  "Create Account",
  "Create Property",
];

export const PERMISSION_MAPPING = {
  addHomeowner: "AddHomeowner",
  editHomeownerDetails: "EditHomeowner",
  deactivateHomeownerAccount: "DeactivateHomeowner",
  addProperty: "AddProperty",
  editProperty: "EditProperty",
  managePropertyEditRequests: "ManagePropertyEditRequests",
  managePropertyDeactivationRequests: "ManagePropertyDeactivationRequests",
  setRevenueGoal: "SetRevenueGoal",
} as const;

export const ADMIN_PERMISSIONS = [
  { id: "addHomeowner", label: "Add a Homeowner" },
  { id: "editHomeownerDetails", label: "Edit Homeowner Details" },
  { id: "deactivateHomeownerAccount", label: "Deactivate Homeowner Account" },
  { id: "addProperty", label: "Add a New Property" },
  { id: "editProperty", label: "Edit Property Details" },
  {
    id: "managePropertyEditRequests",
    label: "Manage Property Edit Requests",
  },
  {
    id: "managePropertyDeactivationRequests",
    label: "Manage Property Deactivation Requests",
  },
  {
    id: "setRevenueGoal",
    label: "Set Revenue Goals",
  },
] as const;

export const permissionMap = new Map<
  BackendPermission,
  { id: string; label: string }
>([
  [
    BackendPermission.AddHomeowner,
    { id: "addHomeowner", label: "Add a Homeowner" },
  ],
  [
    BackendPermission.EditHomeowner,
    { id: "editHomeownerDetails", label: "Edit Homeowner Details" },
  ],
  [
    BackendPermission.DeactivateHomeowner,
    {
      id: "deactivateHomeownerAccount",
      label: "Deactivate Homeowner Account",
    },
  ],
  [
    BackendPermission.AddProperty,
    { id: "addProperty", label: "Add a New Property" },
  ],
  [
    BackendPermission.EditProperty,
    { id: "editProperty", label: "Edit Property Details" },
  ],
  [
    BackendPermission.ManagePropertyEditRequests,
    {
      id: "managePropertyEditRequests",
      label: "Manage Property Edit Requests",
    },
  ],
  [
    BackendPermission.ManagePropertyDeactivationRequests,
    {
      id: "managePropertyDeactivationRequests",
      label: "Manage Property Deactivation Requests",
    },
  ],
  [
    BackendPermission.SetRevenueGoal,
    {
      id: "setRevenueGoal",
      label: "Set Revenue Goals",
    },
  ],
]);

export const TIMER = {
  RESEND_COOLDOWN: 120,
  REMEMBER_ME_COOLDOWN: 1000 * 60 * 60 * 24 * 30,
};

export const CHANNEL_NAMES = {
  GOING_BACK: "goingBack",
  ONBOARDING_CREATE_ACCOUNT_COMPLETE: "onboardingCreateAccountComplete",
  ONBOARDING_CREATE_PROPERTY_COMPLETE: "onboardingCreatePropertyComplete",
  ONBOARDING_SELECT_SINGLE_FAMILY_PROPERTY_TYPE_COMPLETE:
    "onboardingSelectSingleFamilyPropertyTypeComplete",
  ONBOARDING_SELECT_MULTI_FAMILY_PROPERTY_TYPE_COMPLETE:
    "onboardingSelectMultiFamilyPropertyTypeComplete",
};

export const ZXCVBN_OPTIONS = {
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
};

// Maximum length for displaying names before truncation
export const MAX_NAME_LENGTH = 25;
// Maximum length for displaying names before truncation
export const MAX_EMAIL_LENGTH = 30;

export const MAX_MESSAGE_LENGTH = 500; // Maximum characters allowed in a message

export const columnStyles = {
  name: { width: "25%" },
  user: { width: "15%" },
  email: { width: "25%" },
  phone: { width: "15%" },
  activity: { width: "20%" },
  actions: { width: "15%" },
};

export const DOCUMENTS_PAGE_SIZE = 10;

export const UNFINISHED_PROPERTIES_PAGE_SIZE = 10;

export const ACTIVITY_PAGE_COUNT = 10;

export const SUPER_ADMINS_PAGE_COUNT = 10;

export const FULL_BATHROOM_TYPES = [
  { value: "Bidet", label: "Bidet" },
  { value: "Combo Tub Shower", label: "Combo Tub Shower" },
  { value: "Jetted Tub", label: "Jetted Tub" },
  { value: "Outdoor Shower", label: "Outdoor Shower" },
  { value: "Shower", label: "Shower" },
  { value: "Toilet", label: "Toilet" },
  { value: "Tub", label: "Tub" },
];

export const HALF_BATHROOM_TYPES = [
  { value: "Bidet", label: "Bidet" },
  { value: "Toilet", label: "Toilet" },
];

export const BED_TYPES = [
  { value: "Air Mattress", label: "Air Mattress" },
  { value: "Baby Crib", label: "Baby Crib" },
  { value: "Bunk Bed", label: "Bunk Bed" },
  { value: "Bunk Bed Double and Single", label: "Bunk Bed Double and Single" },
  { value: "Child Bed", label: "Child Bed" },
  { value: "Double", label: "Double" },
  { value: "Floor Mattress", label: "Floor Mattress" },
  { value: "Full", label: "Full" },
  { value: "Futon", label: "Futon" },
  { value: "King", label: "King" },
  { value: "Murphy", label: "Murphy" },
  { value: "Queen", label: "Queen" },
  { value: "Sleep Sofa", label: "Sleep Sofa" },
  { value: "Twin", label: "Twin" },
  { value: "Water Bed", label: "Water Bed" },
];

export const MAX_ADDITIONAL_BEDS = 4;

// Upper bound for how many identical beds one bed row can represent. Matches the
// range the bed row offered before the quantity stepper replaced its select.
export const MAX_BED_QUANTITY = 4;
export const MAX_BEDROOMS = 30;

export const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const VENDOR_TYPES = [
  "Landscape",
  "Pest control",
  "Plumber",
  "Hot tub",
  "Pool",
  "Snow Removal",
  "Fire Place Services",
  "Gas Company",
  "Electric Company",
  "Water/Waste Management Company",
  "Scorpion/snake removal",
  "HOA management contact",
  "Internet provider",
  "Streaming TV vendor",
  "Insurance Company",
];

export const MAX_PHOTOS_OUTSIDE_COMPANY_RADIUS = 50;

export const propertyStatusStyles = {
  active: {
    bgColor: "bg-green-200",
    textColor: "text-green-700",
    dotColor: "bg-green-700",
    label: "ACTIVE",
  },
  reviewing: {
    bgColor: "bg-secondary-300",
    textColor: "text-secondary-700",
    dotColor: "bg-secondary-700",
    label: "REVIEWING",
  },
  pending: {
    bgColor: "bg-information-primary-100",
    textColor: "text-primary-500",
    dotColor: "bg-primary-500",
    label: "PENDING",
  },
  archived: {
    bgColor: "bg-gray-200",
    textColor: "text-gray-700",
    dotColor: "bg-gray-700",
    label: "ARCHIVED",
  },
  inactive: {
    bgColor: "bg-red-200",
    textColor: "text-red-700",
    dotColor: "bg-red-700",
    label: "INACTIVE",
  },
  nonRenting: {
    bgColor: "bg-amber-100",
    textColor: "text-amber-700",
    dotColor: "bg-amber-600",
    label: "NON-RENTING",
  },
};

export const propertyStatusMapPinColor = {
  active: {
    primary: colors.green600, // green-600
    secondary: colors.green200, // green-200
  },
  reviewing: {
    primary: colors.secondary700, // secondary-700
    secondary: colors.secondary300, // secondary-300
  },
  pending: {
    primary: colors.primary500, // primary-500
    secondary: colors.informationPrimary100, // information-primary-100
  },
  archived: {
    primary: colors.gray700, // gray-700
    secondary: colors.gray200, // gray-200
  },
  inactive: {
    primary: colors.red600, // red-600
    secondary: colors.red200, // red-200
  },
};

export const IMAGE_URLS = {
  AUTH_LAYOUT_SLIDER1:
    "https://stage-the-compass-collection.s3.us-east-1.amazonaws.com/TCC+LANDING+PAGE+-+SLIDER+1-min.jpg",
  AUTH_LAYOUT_SLIDER2:
    "https://stage-the-compass-collection.s3.us-east-1.amazonaws.com/TCC+LANDING+PAGE+-+SLIDER+2-min.jpg",
  AUTH_LAYOUT_SLIDER3:
    "https://stage-the-compass-collection.s3.us-east-1.amazonaws.com/TCC+LANDING+PAGE+-+SLIDER+3-min.jpg",
} as const;

// Performance Metrics
export const PERFORMANCE_METRICS = {
  RENTAL_REVENUE: "rental_revenue",
  REVPAR: "revpar",
  AVERAGE_DAILY_RATE: "average_daily_rate",
  ADJUSTED_PAID_OCCUPANCY: "adjusted_paid_occupancy",
} as const;

export const PERFORMANCE_METRIC_LABELS = {
  [PERFORMANCE_METRICS.RENTAL_REVENUE]: "Rental Revenue",
  [PERFORMANCE_METRICS.REVPAR]: "RevPAR",
  [PERFORMANCE_METRICS.AVERAGE_DAILY_RATE]: "Average Daily Rate",
  [PERFORMANCE_METRICS.ADJUSTED_PAID_OCCUPANCY]: "Adjusted Paid Occupancy",
} as const;

export const PERFORMANCE_METRIC_FIELDS = {
  [PERFORMANCE_METRICS.RENTAL_REVENUE]: "amount",
  [PERFORMANCE_METRICS.REVPAR]: "revpar",
  [PERFORMANCE_METRICS.AVERAGE_DAILY_RATE]: "averageDailyRate",
  [PERFORMANCE_METRICS.ADJUSTED_PAID_OCCUPANCY]: "adjustedPaidOccupancy",
} as const;

// Chart colors - different color for each metric with gradient support
export const CHART_COLORS = {
  RENTAL_REVENUE: "#3B82F6", // Blue for rental revenue (default)
  REVPAR: "#F97316", // Orange for RevPAR
  AVERAGE_DAILY_RATE: "#8B5CF6", // Purple for Average Daily Rate
  ADJUSTED_PAID_OCCUPANCY: "#10B981", // Green for Adjusted Paid Occupancy
  PRIMARY_LINE: "#16a34a", // Primary green for revenue
} as const;

export const METRIC_COLOR_MAP: Record<string, string> = {
  [PERFORMANCE_METRICS.RENTAL_REVENUE]: CHART_COLORS.RENTAL_REVENUE,
  [PERFORMANCE_METRICS.REVPAR]: CHART_COLORS.REVPAR,
  [PERFORMANCE_METRICS.AVERAGE_DAILY_RATE]: CHART_COLORS.AVERAGE_DAILY_RATE,
  [PERFORMANCE_METRICS.ADJUSTED_PAID_OCCUPANCY]: CHART_COLORS.ADJUSTED_PAID_OCCUPANCY,
} as const;

// Performance Granularity
export const PERFORMANCE_GRANULARITY = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
} as const;

export type PerformanceGranularity = typeof PERFORMANCE_GRANULARITY[keyof typeof PERFORMANCE_GRANULARITY];

export const PERFORMANCE_GRANULARITY_LABELS = {
  [PERFORMANCE_GRANULARITY.DAY]: "Day",
  [PERFORMANCE_GRANULARITY.WEEK]: "Week",
  [PERFORMANCE_GRANULARITY.MONTH]: "Month",
} as const;

// Minimum days required for each granularity option
export const GRANULARITY_MIN_DAYS = {
  [PERFORMANCE_GRANULARITY.DAY]: 1,
  [PERFORMANCE_GRANULARITY.WEEK]: 7,
  [PERFORMANCE_GRANULARITY.MONTH]: 30,
} as const;

// Maximum days allowed for performance date range
export const MAX_PERFORMANCE_DATE_RANGE_DAYS = 365;

// Maximum days allowed for an owner report window (~5 years). Both bounds are
// inclusive, matching the limit the owner report endpoints enforce.
export const MAX_OWNER_REPORT_RANGE_DAYS = 1827;

/**
 * Get valid granularity based on date range.
 * If current granularity is not valid for the date range, returns the best available option.
 */
export const getValidGranularity = (
  daysDifference: number,
  currentGranularity: PerformanceGranularity
): PerformanceGranularity => {
  if (currentGranularity === PERFORMANCE_GRANULARITY.MONTH && daysDifference < GRANULARITY_MIN_DAYS[PERFORMANCE_GRANULARITY.MONTH]) {
    return daysDifference >= GRANULARITY_MIN_DAYS[PERFORMANCE_GRANULARITY.WEEK]
      ? PERFORMANCE_GRANULARITY.WEEK
      : PERFORMANCE_GRANULARITY.DAY;
  }
  if (currentGranularity === PERFORMANCE_GRANULARITY.WEEK && daysDifference < GRANULARITY_MIN_DAYS[PERFORMANCE_GRANULARITY.WEEK]) {
    return PERFORMANCE_GRANULARITY.DAY;
  }
  return currentGranularity;
};

// ============================================================================
// STATEMENTS CONSTANTS
// ============================================================================

/** API endpoint for statements */
export const STATEMENTS_API_ENDPOINT = "/statements";

/** Tab identifiers for the My Documents view */
export const DOCUMENTS_TABS = {
  DOCUMENTS: "documents",
  STATEMENTS: "statements",
} as const;

export type DocumentsTab = typeof DOCUMENTS_TABS[keyof typeof DOCUMENTS_TABS];

/** Default value for "All" filter option in dropdowns */
export const FILTER_ALL_VALUE = "all";

/** Number of years to show in the year filter dropdown */
export const STATEMENTS_YEARS_TO_SHOW = 10;

/** Number of skeleton cards to show while loading statements */
export const STATEMENTS_SKELETON_COUNT = 3;

/** Locale used for formatting statement dates */
export const STATEMENTS_DATE_LOCALE = "en-US";

/** Month options for the statement filter dropdown */
export const MONTHS_OF_YEAR = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
] as const;

/**
 * Generates an array of year options for the statement filter dropdown
 * @param yearsToShow - Number of years to include (defaults to STATEMENTS_YEARS_TO_SHOW)
 * @returns Array of year options with value and label
 */
export const getYearOptions = (yearsToShow: number = STATEMENTS_YEARS_TO_SHOW) => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: yearsToShow }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  }));
};

/** Error messages for statements API operations */
export const STATEMENTS_ERROR_MESSAGES = {
  GET_STATEMENTS_FAILED: "Get statements failed",
  GET_MORE_STATEMENTS_FAILED: "Get more statements failed",
  DOWNLOAD_FAILED: "Failed to download statement. Please try again.",
  SYNC_ALL_FAILED: "Failed to sync statements for all organizations.",
  SYNC_ORGANIZATION_FAILED: "Failed to sync statements for organization.",
} as const;

/** UI labels for the statements feature */
export const STATEMENTS_LABELS = {
  TITLE: "Month-End Statements",
  DESCRIPTION: "View and download your homeowner's monthly property statements.",
  NO_STATEMENTS: "No statements available.",
  LOAD_MORE: "Load more Statements",
  LOADING: "Loading statement...",
  UNKNOWN_SIZE: "Unknown size",
  FILE_TYPE_PREFIX: "PDF",
  FILTER_PROPERTY: "Property",
  FILTER_YEAR: "Year",
  FILTER_MONTH: "Month",
  ALL_PROPERTIES: "All Properties",
  ALL_YEARS: "All Years",
  ALL_MONTHS: "All Months",
  UNKNOWN_PERIOD: "Unknown Period",
} as const;

// =============================================================================
// Property Creation Settings
// =============================================================================

/**
 * Property creation feature keys - used for configuring which features
 * are required for homeowners vs handled by the organization
 */
export const PROPERTY_CREATION_FEATURES = {
  SELECT_PROPERTY_TYPE: "SELECT_PROPERTY_TYPE",
  EXISTING_PROPERTY_LISTINGS: "EXISTING_PROPERTY_LISTINGS",
  PROPERTY_INFORMATION: "PROPERTY_INFORMATION",
  PROPERTY_DETAILS: "PROPERTY_DETAILS",
  PROPERTY_FEATURES: "PROPERTY_FEATURES",
  PROPERTY_DESCRIPTION: "PROPERTY_DESCRIPTION",
  VENDOR_INFORMATION: "VENDOR_INFORMATION",
  ADDITIONAL_INFORMATION: "ADDITIONAL_INFORMATION",
  PROFESSIONAL_PHOTOS: "PROFESSIONAL_PHOTOS",
  SIGN_DOCUMENTS: "SIGN_DOCUMENTS",
  UPLOAD_DOCUMENTS: "UPLOAD_DOCUMENTS",
  CREATE_CALENDAR_BLOCK: "CREATE_CALENDAR_BLOCK",
} as const;

/** Property creation assignment types */
export const PROPERTY_CREATION_ASSIGNMENTS = {
  HOMEOWNER: "HOMEOWNER",
  ORGANIZATION: "ORGANIZATION",
} as const;

/**
 * Display labels for property creation features
 * Maps feature keys to human-readable labels
 */
export const PROPERTY_CREATION_FEATURE_LABELS: Record<
  keyof typeof PROPERTY_CREATION_FEATURES,
  string
> = {
  SELECT_PROPERTY_TYPE: "Select Property Type",
  EXISTING_PROPERTY_LISTINGS: "Existing Property Listings",
  PROPERTY_INFORMATION: "Property Information",
  PROPERTY_DETAILS: "Property Details",
  PROPERTY_FEATURES: "Property Features",
  PROPERTY_DESCRIPTION: "Property Description",
  VENDOR_INFORMATION: "Vendor Information",
  ADDITIONAL_INFORMATION: "Additional Information",
  PROFESSIONAL_PHOTOS: "Professional Photos",
  SIGN_DOCUMENTS: "Sign Documents",
  UPLOAD_DOCUMENTS: "Upload Documents",
  CREATE_CALENDAR_BLOCK: "Create Calendar Block",
} as const;

/**
 * Ordered list of property creation features with their display labels
 * Used for rendering feature lists in consistent order
 */
export const PROPERTY_CREATION_FEATURES_LIST = [
  { key: PROPERTY_CREATION_FEATURES.SELECT_PROPERTY_TYPE, label: PROPERTY_CREATION_FEATURE_LABELS.SELECT_PROPERTY_TYPE },
  { key: PROPERTY_CREATION_FEATURES.EXISTING_PROPERTY_LISTINGS, label: PROPERTY_CREATION_FEATURE_LABELS.EXISTING_PROPERTY_LISTINGS },
  { key: PROPERTY_CREATION_FEATURES.PROPERTY_INFORMATION, label: PROPERTY_CREATION_FEATURE_LABELS.PROPERTY_INFORMATION },
  { key: PROPERTY_CREATION_FEATURES.PROPERTY_DETAILS, label: PROPERTY_CREATION_FEATURE_LABELS.PROPERTY_DETAILS },
  { key: PROPERTY_CREATION_FEATURES.PROPERTY_FEATURES, label: PROPERTY_CREATION_FEATURE_LABELS.PROPERTY_FEATURES },
  { key: PROPERTY_CREATION_FEATURES.PROPERTY_DESCRIPTION, label: PROPERTY_CREATION_FEATURE_LABELS.PROPERTY_DESCRIPTION },
  { key: PROPERTY_CREATION_FEATURES.VENDOR_INFORMATION, label: PROPERTY_CREATION_FEATURE_LABELS.VENDOR_INFORMATION },
  // ADDITIONAL_INFORMATION - Not shown (feature commented out in the code)
  // SIGN_DOCUMENTS - Not shown (only homeowners can sign documents)
  { key: PROPERTY_CREATION_FEATURES.PROFESSIONAL_PHOTOS, label: PROPERTY_CREATION_FEATURE_LABELS.PROFESSIONAL_PHOTOS },
  { key: PROPERTY_CREATION_FEATURES.UPLOAD_DOCUMENTS, label: PROPERTY_CREATION_FEATURE_LABELS.UPLOAD_DOCUMENTS },
  { key: PROPERTY_CREATION_FEATURES.CREATE_CALENDAR_BLOCK, label: PROPERTY_CREATION_FEATURE_LABELS.CREATE_CALENDAR_BLOCK },
] as const;

/** UI labels for property creation settings */
export const PROPERTY_CREATION_SETTINGS_LABELS = {
  TITLE: "Property Creation Settings",
  MODAL_TITLE: "Property Creation Settings",
  EDIT_BUTTON: "Edit Settings",
  SAVE_BUTTON: "Save Settings",
  CANCEL_BUTTON: "Cancel",
  TABLE_HEADER_FEATURE: "Property Creation Feature",
  TABLE_HEADER_HOMEOWNER: "Homeowner",
  HOMEOWNER_REQUIREMENTS: "Homeowner Requirements:",
  ORGANIZATION_REQUIREMENTS_SUFFIX: " Requirements:",
  NO_SETTINGS_MESSAGE: "No property creation settings configured. Click Edit Settings to configure which features are required for homeowners vs handled by the organization.",
  SUCCESS_MESSAGE: "Property creation settings have been updated successfully",
  ERROR_MESSAGE: "Failed to update property creation settings",
} as const;

/** Error messages for property creation settings API operations */
export const PROPERTY_CREATION_SETTINGS_ERRORS = {
  GET_SETTINGS_FAILED: "Get property creation settings failed",
  UPDATE_SETTINGS_FAILED: "Update property creation settings failed",
  FETCH_SETTINGS_FAILED: "Failed to fetch settings",
} as const;

// =============================================================================
// Property Page Filter Options
// =============================================================================

export const DEFAULT_BEDROOM_COUNT = "0";

export const PROPERTY_STATUS_FILTER_IDS = {
  ACTIVE: "active",
  ARCHIVED: "archived",
  PENDING: "pending",
  NON_RENTING: "nonRenting",
} as const;

/**
 * Filter options for property status on the Properties page.
 * Maps user-friendly filter names to database PropertyStatus values.
 */
export const PROPERTY_STATUS_FILTER_OPTIONS: FilterStatus[] = [
  {
    id: PROPERTY_STATUS_FILTER_IDS.ACTIVE,
    name: "Active",
    selected: true,
    statuses: [PropertyStatus.ACTIVE],
  },
  {
    id: PROPERTY_STATUS_FILTER_IDS.ARCHIVED,
    name: "Archived",
    selected: false,
    statuses: [PropertyStatus.ARCHIVED],
  },
  {
    id: PROPERTY_STATUS_FILTER_IDS.PENDING,
    name: "Pending",
    selected: false,
    statuses: ["PENDING" as PropertyStatus],
  },
  {
    id: PROPERTY_STATUS_FILTER_IDS.NON_RENTING,
    name: "Non-Renting",
    selected: false,
    statuses: [PropertyStatus.NON_RENTING],
  },
];
